import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import './TableToolbar.css';

// The search input stays responsive (local state) while `onSearchChange` —
// which typically drives a server query or a list filter — fires only after
// the user pauses. One shared debounce for every list table.
const SEARCH_DEBOUNCE_MS = 250;

// DensityToggle removed — wasn't part of the original recepthor app and
// added visual noise to every list page. Keep the export so existing
// imports (if any) don't break, but render nothing.
export function DensityToggle() {
  return null;
}

DensityToggle.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export function TableToolbar({
  searchPlaceholder,
  chips,
  activeChips,
  onChipToggle,
  searchValue,
  onSearchChange,
  right,
}) {
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  // The sticky table header sits directly below this toolbar. Its height isn't
  // fixed — chips wrap on narrow viewports — so publish the real height to
  // `--table-toolbar-h`; `.ledger thead th` sticks at topbar + this.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const publish = () =>
      document.documentElement.style.setProperty(
        '--table-toolbar-h',
        `${el.offsetHeight}px`,
      );
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Local input value keeps typing instant; the debounced `onSearchChange` is
  // what re-queries/filters. `lastEmittedRef` lets an EXTERNAL change to
  // `searchValue` (e.g. a programmatic reset) re-sync the input without
  // clobbering in-flight keystrokes (our own debounced emits are ignored here).
  const [localValue, setLocalValue] = useState(searchValue ?? '');
  const lastEmittedRef = useRef(searchValue ?? '');
  const debounceRef = useRef(null);

  useEffect(() => {
    const next = searchValue ?? '';
    if (next !== lastEmittedRef.current) {
      lastEmittedRef.current = next;
      setLocalValue(next);
    }
  }, [searchValue]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  function handleSearchChange(value) {
    setLocalValue(value);
    if (!onSearchChange) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      lastEmittedRef.current = value;
      onSearchChange(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  // Pressing '/' anywhere outside an input/textarea focuses the search box.
  useEffect(() => {
    if (!onSearchChange) return undefined;
    function onKey(e) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const tag = t?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        t?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      inputRef.current?.focus();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onSearchChange]);

  const searchActive = !!onSearchChange;

  return (
    <div ref={rootRef} className="table-toolbar">
      <div
        className={`table-toolbar__search${searchActive ? ' table-toolbar__search--active' : ''}`}
        role="search"
        aria-label="Buscar en tabla"
      >
        <span className="table-toolbar__search-icon" aria-hidden="true">
          ⌕
        </span>
        {searchActive ? (
          <input
            ref={inputRef}
            type="search"
            className="table-toolbar__search-input"
            placeholder={searchPlaceholder}
            value={localValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label={searchPlaceholder}
          />
        ) : (
          <span className="table-toolbar__search-placeholder">
            {searchPlaceholder}
          </span>
        )}
        <kbd className="table-toolbar__search-kbd">/</kbd>
      </div>

      {chips && chips.length > 0 && (
        <div className="table-toolbar__chips" role="group" aria-label="Filtros">
          {chips.map((chip) => {
            const isActive = activeChips
              ? activeChips.includes(chip.key)
              : false;
            return (
              <button
                key={chip.key}
                type="button"
                className={`table-toolbar__chip${isActive ? ' table-toolbar__chip--active' : ''}`}
                aria-pressed={isActive}
                onClick={() => onChipToggle && onChipToggle(chip.key)}
              >
                {chip.label}
                {chip.count != null && (
                  <span className="table-toolbar__chip-count">
                    {chip.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="table-toolbar__spacer" />

      {right && right}
    </div>
  );
}

TableToolbar.propTypes = {
  searchPlaceholder: PropTypes.string,
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
    }),
  ),
  activeChips: PropTypes.arrayOf(PropTypes.string),
  onChipToggle: PropTypes.func,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  right: PropTypes.node,
};

TableToolbar.defaultProps = {
  searchPlaceholder: 'Buscar…',
  chips: [],
  activeChips: [],
  density: 'compact',
  onDensityChange: () => {},
};
